import { z } from "zod";
/**
 * Contract for messages on the SQS email queue (`<env>-email-queue`).
 *
 * This is the wire format shared by the producer (`apps/api`) and the consumer
 * (`apps/worker`): the API enqueues one of these, the worker validates the body
 * against `emailMessageSchema`, renders the matching template, and sends via the
 * email provider. Keep it provider-agnostic — no SQS or Resend types belong here.
 *
 * Adding an email: add a member to the discriminated union below and a matching
 * entry in the worker's template registry. The `type` discriminant ties the two
 * together.
 */
/** Address-request invitation: asks a contact to confirm/provide their mailing address. */
export const addressRequestEmailDataSchema = z.object({
    /** Display name of the contact receiving the request (for the greeting). */
    contactName: z.string().min(1),
    /** Name of the account holder asking, shown as who the request is from. */
    requesterName: z.string().min(1),
    /** Absolute URL of the pre-filled share link the contact opens to respond. */
    shareUrl: z.string().url(),
    /** ISO-8601 expiry of the share link, if any (rendered as a deadline). */
    expiresAt: z.string().datetime().nullable().optional(),
});
/** Password reset: sends the account holder a time-limited link to set a new password. */
export const passwordResetEmailDataSchema = z.object({
    /** Display name of the account holder (for the greeting); null when unset. */
    name: z.string().nullable(),
    /** Absolute URL the user opens to choose a new password. */
    resetUrl: z.string().url(),
    /** ISO-8601 expiry of the reset link (rendered as a deadline). */
    expiresAt: z.string().datetime(),
});
/** Welcome email sent after a new account signs up. */
export const accountCreatedEmailDataSchema = z.object({
    /** Display name of the new account holder (for the greeting); null when unset. */
    name: z.string().nullable(),
    /** Absolute URL of the app, linked as the get-started CTA. */
    appUrl: z.string().url(),
});
/** Account deletion: confirms to the user that their account and data were removed. */
export const accountDeletedEmailDataSchema = z.object({
    /** Display name of the (now deleted) account holder; null when unset. */
    name: z.string().nullable(),
});
export const emailMessageSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("address_request"),
        to: z.string().email(),
        data: addressRequestEmailDataSchema,
    }),
    z.object({
        type: z.literal("password_reset"),
        to: z.string().email(),
        data: passwordResetEmailDataSchema,
    }),
    z.object({
        type: z.literal("account_created"),
        to: z.string().email(),
        data: accountCreatedEmailDataSchema,
    }),
    z.object({
        type: z.literal("account_deleted"),
        to: z.string().email(),
        data: accountDeletedEmailDataSchema,
    }),
]);
//# sourceMappingURL=email.js.map